import React from "react";
import { Dispatcher } from "../../flux";
import ChapterCards from "./chapterCards";
import ChapterFigure from "./chapterFigure";
import ChapterResults from "./chapterResults";
import FiguresOverlay from "./overlay";
import Header from "./header";
import { SearchField } from "../../components";
import { Button } from "@vitalsource/vstui";

export default class Chapters extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: null,
      chapters: [],
      findValue: ""
    };

    this._matchingFigures = this._matchingFigures.bind(this);
    this._changeFind = this._changeFind.bind(this);
    this._enlargeFigure = this._enlargeFigure.bind(this);
    this._goToNextFigure = this._goToNextFigure.bind(this);
    this._goToPreviousFigure = this._goToPreviousFigure.bind(this);
    this._closeFigure = this._closeFigure.bind(this);
    this._closeFiguresPanel = this._closeFiguresPanel.bind(this);
  }

  UNSAFE_componentWillUpdate(np, ns) {
    const { findValue: nextFindValue } = ns;
    const { findValue } = this.state;

    if (findValue !== nextFindValue) {
      this.lastMatchingFigures = null;
    }
  }

  _getChapters() {
    const chapters = [];
    let oldChapterFigures;
    let currentChapterFigures = [];
    const matchingFigures = this._matchingFigures() || [];
    if (matchingFigures.length === 0) {
      return matchingFigures;
    }
    let lastChapterTitle = matchingFigures[0].chapterTitle;
    matchingFigures.forEach(figure => {
      if (lastChapterTitle === figure.chapterTitle) {
        currentChapterFigures.push(figure);
      } else if (
        (oldChapterFigures = chapters.find(
          chapter => chapter.title === figure.chapterTitle
        ))
      ) {
        oldChapterFigures.figures.push(figure);
        return;
      } else {
        chapters.push({
          title: lastChapterTitle,
          figures: currentChapterFigures
        });
        currentChapterFigures = [figure];
      }
      lastChapterTitle = figure.chapterTitle;
    });

    chapters.push({ title: lastChapterTitle, figures: currentChapterFigures });
    return chapters;
  }

  _closeFiguresPanel() {
    Dispatcher.emit(Dispatcher.TOGGLE_PANEL, "figures");
  }

  _changeFind(findValue) {
    this.setState({ findValue });
    Fraction.Helpers.delayTimeout(
      function() {
        Fraction.Reporter.gaEvent("reader/panel/figures/find", findValue);
      }.bind(this),
      2500,
      "figures-find-figure"
    );
  }

  _attributeMatchesFindValue(attr) {
    return (
      attr &&
      attr.toLowerCase().indexOf(this.state.findValue.toLowerCase()) !== -1
    );
  }

  _matchingFigures() {
    if (this.lastMatchingFigures && this.lastMatchingFigures.length) {
      return this.lastMatchingFigures;
    }
    const { figures } = this.props;
    if (this.state.findValue && figures) {
      this.lastMatchingFigures = figures.filter(figure => {
        const { title, caption } = figure;
        if (
          this._attributeMatchesFindValue(title) ||
          this._attributeMatchesFindValue(caption)
        ) {
          return figure;
        }
      });
    } else {
      this.lastMatchingFigures = figures;
    }
    return this.lastMatchingFigures;
  }

  _enlargeFigure(activeIndex) {
    this.setState({ activeIndex });
  }

  _goToNextFigure() {
    const max = this._matchingFigures().length - 1;
    this.setState(prevState => {
      if (prevState.activeIndex === max) {
        return { activeIndex: 0 };
      } else {
        return { activeIndex: prevState.activeIndex + 1 };
      }
    });
  }

  _goToPreviousFigure() {
    this.setState(prevState => {
      if (prevState.activeIndex === 0) {
        return { activeIndex: this._matchingFigures().length - 1 };
      } else {
        return { activeIndex: prevState.activeIndex - 1 };
      }
    });
  }

  _closeFigure() {
    this.setState({ activeIndex: null });
  }

  _activeFigure() {
    const { activeIndex } = this.state;
    let activeFigure;
    if (!activeIndex && activeIndex !== 0) {
      return;
    }
    let remaining = activeIndex;
    const chapters = this._getChapters();
    for (let i = 0; i < chapters.length; i++) {
      const chapter = chapters[i];
      if (remaining >= chapter.figures.length) {
        remaining -= chapter.figures.length;
      } else {
        return chapter.figures[remaining];
      }
    }
  }

  _backToMatchingFigures() {
    this.setState({ activeIndex: null });
  }

  _renderSearchBar() {
    return (
      <div className="search-header panel-header-container">
        <SearchField
          onChange={this._changeFind}
          value={this.state.findValue}
          placeholder={i18n.t("reader.panels.find-a-figure")}
          reset={this._reset}
          search={this._searchBook}
          incremental
        />
      </div>
    );
  }

  render() {
    const { enlargeFigure, figures, isbn, sortOrder, onSelect } = this.props;
    const { activeIndex, findValue } = this.state;
    const matchingFigures = this._matchingFigures() || [];
    const chapters = this._getChapters();
    if (activeIndex || activeIndex === 0) {
      return (
        <FiguresOverlay
          activeFigure={
            findValue ? this._activeFigure() : matchingFigures[activeIndex]
          }
          backtoMatchingFigures={this._backToMatchingFigures}
          previous={this._goToPreviousFigure}
          close={this._closeFigure}
          next={this._goToNextFigure}
          isbn={this.props.isbn}
        />
      );
    }
    return (
      <div id="figures-container" className="figures-panel">
        <div className="header noselect">
          <h2
            id="panel-header-figures"
            className={
              "panel-title no-highlight " +
              (typeof this.props.title === "object" ? "has-content" : "")
            }
            tabIndex={-1}
          >
            {i18n.t("reader.panels.figures") || this.props.title}
          </h2>
          <Button
            className="panel-close-button"
            onClick={this._closeFiguresPanel}
            naked
          >
            <span>
              {i18n.t("reader.panels.close-panel", {
                panelName: i18n.t("reader.panels.figures")
              })}
            </span>
          </Button>
        </div>
        {this._renderSearchBar()}
        <Header
          count={matchingFigures.length}
          sortOrder={sortOrder}
          onSelect={onSelect}
        />
        {findValue ? (
          <ChapterResults
            activeFigure={this._activeFigure()}
            chapters={chapters}
            enlargeFigure={this._enlargeFigure}
            figures={matchingFigures}
            goToNextFigure={this._goToNextFigure}
            goToPreviousFigure={this._goToPreviousFigure}
            closeFigure={this._closeFigure}
            isbn={this.props.isbn}
            sortOrder={sortOrder}
          />
        ) : (
          <ChapterCards
            chapters={chapters}
            selectFigures={this.props.selectFigures}
            sortOrder={sortOrder}
          />
        )}
      </div>
    );
  }
}
